<template>
  <div
    class="products-with-show-more-btn"
    :class="{'page-container': !withoutContainer}"
  >
    <header class="products-with-show-more-btn__header">
      <h3 class="products-with-show-more-btn__title">{{ title }} <template v-if="computedCountOfProducts">{{ `(${computedCountOfProducts})` }}</template></h3>
      <a
        v-if="link"
        @click.stop="handleLinkClick"
        :href="link"
        class="products-with-show-more-btn__more">
        {{ computedLinkTitle }}
      </a>
    </header>
    <category-filter
      class="products-with-show-more-btn__filters"
      without-container
      :data="filterData"
      @change-filter="handleChangeFilter"></category-filter>
    <products-simple-list
      @cardInViewportEvent="collectAnalyticsData($event)"
      :products="computedProducts"
      :title-ru="titleRu"
      :title-analytics="titleAnalytics"
      :shown-skeletons="shownSkeletons"
      :esputnik-block-id="esputnikBlockId"
      class="page-container__products products-with-show-more-btn__category-products">
      <template #handler>
        <li
          class="category-products__card category-products__card--btn"
          v-if="isShowMoreBtnVisible">
          <Button
            @click.native="handleBtnClick"
            type="plain">{{ $t('main_show_more') }}</Button>
        </li>
      </template>
    </products-simple-list>
  </div>
</template>

<script>
import api from '@/api'

import { transliteration } from '@/mixins/transliteration'

import CategoryFilter from './CategoryFilter'
import Button from '../../inputs/Button'
import ProductsSimpleList from '@/components/product/product-block/ProductsSimpleList'
import { GoogleEnhancedEcommerce } from '@/services/AnalyticService'

export default {
  name: 'ProductsWithShowMoreBtn',
  mixins: [transliteration],
  components: {
    ProductsSimpleList,
    CategoryFilter,
    Button
  },
  props: {
    id: {
      type: [String, Number],
      required: true
    },
    title: {
      type: String,
      default: ''
    },
    titleRu: {
      type: String,
      default: ''
    },
    titleAnalytics: {
      type: String,
      default: ''
    },
    link: {
      type: String,
      default: ''
    },
    linkTitle: {
      type: String,
      default: ''
    },
    products: {
      type: Array,
      default: () => []
    },
    linkClickPrevent: {
      type: Boolean,
      default: false
    },
    showFilter: {
      type: Boolean,
      default: false
    },
    filterData: {
      type: Array,
      default: () => []
    },
    isServerPagination: {
      type: Boolean,
      default: false
    },
    dataForFixedBlock: {
      type: Object,
      default: () => {}
    },
    withoutContainer: {
      type: Boolean,
      default: false
    },
    esputnikBlockId: String
  },
  computed: {
    initialProductsShowCount () {
      return this.currentResolution <= 550 ? 2 : this.currentResolution <= 1023 ? 3 : this.currentResolution <= 1299 ? 4 : 5
    },
    computedOffset () {
      return this.initialProductsShowCount + this.offset
    },
    filteredProducts () {
      return this.products.filter(item => this.currentCategory ? item.category_id === this.currentCategory : item)
    },
    computedProducts () {
      if (this.isServerPagination) {
        return this.initialData ? this.loadedProducts.slice(0, this.initialProductsShowCount) : this.loadedProducts
      }

      return this.filteredProducts.slice(0, this.computedOffset)
    },
    isShowMoreBtnVisible () {
      if (this.isServerPagination) {
        return (this.initialData && this.initialProductsShowCount < this.loadedInfo?.['goods_all']) ? true : !!this.loadedInfo?.['goods_left']
      }

      return this.filteredProducts.length > this.computedOffset
    },
    computedLinkTitle () {
      return this.linkTitle || this.$t('see_all')
    },
    computedCountOfProducts () {
      if (this.isServerPagination) {
        return this.loadedInfo?.['goods_all'] ?? null
      }

      return this.products.length
    }
  },
  data () {
    return {
      currentResolution: window.innerWidth,
      currentCategory: null,
      offset: 0,
      step: 10,
      loadedInfo: this.dataForFixedBlock,
      loadedProducts: this.products,
      isLoading: false,
      shownSkeletons: 0,
      initialData: true,
      collectedAnalyticsData: []
    }
  },
  methods: {
    async sendAnalyticsData () {
      if (GoogleEnhancedEcommerce.isEnabled) {
        if (this.collectedAnalyticsData.length) {
          await GoogleEnhancedEcommerce.viewItemList({
            analyticsData: this.collectedAnalyticsData,
            listId: this.id,
            listName: this.titleAnalytics
          })
          this.collectedAnalyticsData = []
        }
      }
    },
    collectAnalyticsData (el) {
      this.collectedAnalyticsData.push(el)
    },
    calculateCurrentResolution () {
      this.currentResolution = window.innerWidth
    },
    handleOffset () {
      this.offset = this.offset + this.step
    },
    handleLinkClick (e) {
      if (this.linkClickPrevent) {
        e.preventDefault()
        this.$emit('link-click')
      }
    },
    handleChangeFilter (e) {
      this.offset = 0
      this.currentCategory = e.id
    },
    loadMoreProducts () {
      this.isLoading = true

      return new Promise(resolve => {
        api.blocks.getFixedBlockProducts({
          id: this.id,
          offset: this.loadedInfo?.offset,
          categoryId: this.currentCategory
        }).then(res => {
          this.isLoading = false

          if (res.success) {
            this.loadedInfo = res
            this.loadedProducts = [...this.loadedProducts, ...res.data]
          }

          resolve(res)
        })
      })
    },
    async handleBtnClick () {
      if (this.isServerPagination) {
        if (this.initialData && this.initialProductsShowCount < this.loadedInfo?.['goods_all']) {
          this.initialData = false
        } else {
          await this.loadMoreProducts()
          this.initialData = false
        }
      } else {
        this.handleOffset()
      }
    }
  },
  watch: {
    collectedAnalyticsData () {
      setTimeout(() => {
        this.sendAnalyticsData()
      }, 1000)
    },
    currentCategory () {
      if (this.isServerPagination) {
        this.loadedInfo = {}
        this.loadedProducts = []
        this.initialData = true
        this.loadMoreProducts()
      }
    },
    isLoading (value) {
      if (value) {
        this.shownSkeletons = 10
      } else {
        this.shownSkeletons = 0
      }
    }
  },
  mounted () {
    window.addEventListener('resize', this.calculateCurrentResolution)
  },
  destroyed () {
    window.removeEventListener('resize', this.calculateCurrentResolution)
  }
}
</script>

<style scoped lang="sass">

.page-container__products,
.category-products-wrap__item
  margin-bottom: 0

.category-products__card--skeleton-mobile
  padding: 15px 10px
  display: block

  +mmin($tablet)
    display: none

.category-products__card--skeleton-desktop
  display: none

  +mmin($tablet)
    display: block

.products-with-show-more-btn
  min-height: 765px
  +mmin(360)
    min-height: 443px
  +mmin(768)
    min-height: 494px
.products-with-show-more-btn__header
  display: flex
  flex-flow: row wrap
  justify-content: space-between
  align-items: baseline
  margin: 0 0 15px

  +mmin($tablet)
    justify-content: flex-start
    margin: 0 0 30px

.products-with-show-more-btn__title
  font-size: 18px
  font-weight: 500
  padding: 0
  margin: 0

  +mmin($tablet)
    font-size: 24px

.products-with-show-more-btn__more
  color: $main-color
  font-size: 14px
  font-weight: 400 //Rubik 300

  +mmin($tablet)
    font-size: 16px
    margin-left: 20px

.category-products__card--btn
  width: 100%!important
  border: none!important
  align-self: center
  padding: 20px 0 0
  margin: 0 -1px
  background-color: $white-text-color

  button
    margin: 0 auto

.products-with-show-more-btn__filters
  margin-bottom: 20px
</style>
